import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Container, Grid, Typography, CircularProgress, Stack, Chip, Button,
    IconButton, Tooltip, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar
} from 'recharts';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import LanguageIcon from '@mui/icons-material/Language';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StorageIcon from '@mui/icons-material/Storage';

// ─── Chart Color Palette ─────────────────────────────────────────────
const CHART_COLORS = ['#3558a4', '#1ecab8', '#50b5ff', '#ffa91f', '#f1646c', '#a855f7', '#6366f1'];

// ─── Custom Recharts Tooltip ─────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{
            bgcolor: 'rgba(49, 58, 70, 0.92)', backdropFilter: 'blur(8px)',
            borderRadius: 2, px: 2, py: 1.5, border: '1px solid rgba(255,255,255,0.08)'
        }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            {payload.map((entry, i) => (
                <Typography key={i} variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                    {entry.name}: {Number(entry.value).toLocaleString()}
                </Typography>
            ))}
        </Box>
    );
};

// ─── Scorecard Component ─────────────────────────────────────────────
const Scorecard = ({ title, value, subtitle, icon, color, trend }) => {
    const theme = useTheme();
    const isPositive = trend >= 0;
    return (
        <Box sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: 3, p: 3, height: '100%',
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
            position: 'relative', overflow: 'hidden',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 40px ${alpha(color, 0.15)}`,
                borderColor: alpha(color, 0.3),
            },
            '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0,
                width: '100%', height: '3px',
                background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})`,
            }
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                        {title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}>
                        {value}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        {trend !== undefined && (
                            <Chip
                                size="small"
                                icon={isPositive ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                                label={`${isPositive ? '+' : ''}${trend}%`}
                                sx={{
                                    height: 22, fontSize: '0.7rem', fontWeight: 700,
                                    bgcolor: alpha(isPositive ? '#1ecab8' : '#f1646c', 0.1),
                                    color: isPositive ? '#1ecab8' : '#f1646c',
                                    '& .MuiChip-icon': { color: 'inherit' }
                                }}
                            />
                        )}
                        {subtitle && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Stack>
                </Box>
                <Box sx={{
                    bgcolor: alpha(color, 0.08), color, p: 1.5, borderRadius: 2.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.3s ease',
                }}>
                    {icon}
                </Box>
            </Stack>
        </Box>
    );
};

// ─── Section Card Wrapper ────────────────────────────────────────────
const SectionCard = ({ children, title, subtitle, action, sx = {} }) => {
    const theme = useTheme();
    return (
        <Box sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: 3, p: 3, height: '100%',
            border: `1px solid ${theme.palette.divider}`,
            transition: 'box-shadow 0.3s ease',
            '&:hover': { boxShadow: '0 4px 24px rgba(49,58,70,0.06)' },
            ...sx
        }}>
            {(title || action) && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>{title}</Typography>
                        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
                    </Box>
                    {action}
                </Stack>
            )}
            {children}
        </Box>
    );
};

// ─── Donut Center Label ──────────────────────────────────────────────
const DonutCenterLabel = ({ viewBox, total }) => {
    const { cx, cy } = viewBox;
    return (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
            <tspan x={cx} y={cy - 8} style={{ fontSize: '1.4rem', fontWeight: 700, fill: '#313a46' }}>
                {total.toLocaleString()}
            </tspan>
            <tspan x={cx} y={cy + 14} style={{ fontSize: '0.7rem', fill: '#6c757d' }}>
                Total Words
            </tspan>
        </text>
    );
};

// ─── Reporting Dialog ────────────────────────────────────────────────
const ReportingDialog = ({ open, onClose }) => {
    const [email, setEmail] = useState('');
    const [frequency, setFrequency] = useState('weekly');
    const [format, setFormat] = useState('pdf');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSaving(false);
        onClose();
        alert('Report settings saved! (Simulation)');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 600 }}>Export & Schedule Reports</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Recipients</Typography>
                        <TextField fullWidth placeholder="Enter email addresses..." value={email}
                            onChange={(e) => setEmail(e.target.value)} size="small" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Frequency</Typography>
                        <Stack direction="row" spacing={1}>
                            {['weekly', 'monthly', 'on-demand'].map((f) => (
                                <Chip key={f} label={f.charAt(0).toUpperCase() + f.slice(1)}
                                    color={frequency === f ? 'primary' : 'default'}
                                    onClick={() => setFrequency(f)} clickable />
                            ))}
                        </Stack>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Format</Typography>
                        <Stack direction="row" spacing={1}>
                            {['pdf', 'csv'].map((f) => (
                                <Chip key={f} label={f.toUpperCase()}
                                    color={format === f ? 'primary' : 'default'}
                                    onClick={() => setFormat(f)} clickable />
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="text">Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ═════════════════════════════════════════════════════════════════════
// ─── MAIN ANALYTICS PAGE ────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════
export default function AnalyticsPage() {
    const theme = useTheme();
    const token = useSelector((state) => state.auth.token);

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [timeRange, setTimeRange] = useState(30);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await axios.get('/stats', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStats(res.data);
            } catch (err) {
                console.error('Error fetching stats:', err);
                setError('Failed to load analytics data.');
            } finally {
                setLoading(false);
            }
        };
        if (token) fetchStats();
    }, [token]);

    const {
        wordsTranslated = 0, completedProjects = 0, activeProjects = 0,
        topLanguages = [], activityOverTime = [], recentProjects = [],
        translationMemoryCount = 0
    } = stats || {};

    // Fill missing dates for chart continuity
    const chartData = useMemo(() => {
        const filled = [];
        const today = new Date();
        const dataMap = new Map(activityOverTime.map(item => [item.date, item]));
        for (let i = timeRange - 1; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().split('T')[0];
            if (dataMap.has(key)) {
                const entry = dataMap.get(key);
                filled.push({ ...entry, label: formatDateLabel(d) });
            } else {
                filled.push({ date: key, words: 0, projects: 0, label: formatDateLabel(d) });
            }
        }
        return filled;
    }, [activityOverTime, timeRange]);

    // Donut data
    const donutData = useMemo(() => {
        return topLanguages.map((lang, i) => ({
            name: lang.name || 'Unknown',
            value: lang.count,
            projects: lang.projects,
            color: CHART_COLORS[i % CHART_COLORS.length]
        }));
    }, [topLanguages]);

    const totalLangWords = donutData.reduce((sum, d) => sum + d.value, 0);

    // Table pagination state
    const [tablePage, setTablePage] = useState(0);
    const rowsPerPage = 5;

    // ─── Loading State ───────────────────────────────────────────────
    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Skeleton variant="text" width={280} height={40} sx={{ mb: 1 }} />
                <Skeleton variant="text" width={350} height={24} sx={{ mb: 4 }} />
                <Grid container spacing={3} mb={4}>
                    {[1, 2, 3, 4].map(i => (
                        <Grid item xs={12} sm={6} md={3} key={i}>
                            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
                    </Grid>
                </Grid>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Typography color="error">{error}</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <ReportingDialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} />

            {/* ─── Header ─────────────────────────────────────────── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={4}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track your translation productivity and efficiency.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}
                            startAdornment={<CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />}
                            sx={{ borderRadius: 2, fontSize: '0.85rem', fontWeight: 600,
                                bgcolor: theme.palette.background.paper }}>
                            <MenuItem value={7}>Last 7 days</MenuItem>
                            <MenuItem value={14}>Last 14 days</MenuItem>
                            <MenuItem value={30}>Last 30 days</MenuItem>
                            <MenuItem value={90}>Last 90 days</MenuItem>
                            <MenuItem value={180}>Last 6 months</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="outlined" startIcon={<DownloadIcon />}
                        onClick={() => setReportDialogOpen(true)}
                        sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
                        Export
                    </Button>
                </Stack>
            </Stack>

            {/* ─── KPI Scorecards ─────────────────────────────────── */}
            <Grid container spacing={2.5} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <Scorecard title="Total Words" value={wordsTranslated.toLocaleString()}
                        subtitle="across all projects" icon={<TrendingUpIcon />}
                        color={theme.palette.primary.main} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Scorecard title="Completed" value={completedProjects}
                        subtitle="projects delivered" icon={<AssignmentTurnedInIcon />}
                        color={theme.palette.success.main} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Scorecard title="In Progress" value={activeProjects}
                        subtitle="active projects" icon={<PendingActionsIcon />}
                        color={theme.palette.warning.main} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Scorecard title="TM Entries" value={translationMemoryCount.toLocaleString()}
                        subtitle="memory segments" icon={<StorageIcon />}
                        color={theme.palette.info.main} />
                </Grid>
            </Grid>

            {/* ─── Charts Row ─────────────────────────────────────── */}
            <Grid container spacing={2.5} mb={3}>
                {/* Area Chart */}
                <Grid item xs={12} md={8}>
                    <SectionCard title="Translation Volume" subtitle={`Last ${timeRange} days`}>
                        {chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="wordsGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.25} />
                                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                    <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        axisLine={false} tickLine={false} width={50}
                                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                                    <RTooltip content={<CustomTooltip />} />
                                    <Area type="monotone" dataKey="words" name="Words"
                                        stroke={theme.palette.primary.main} strokeWidth={2.5}
                                        fill="url(#wordsGrad)" dot={false}
                                        activeDot={{ r: 5, fill: theme.palette.primary.main, stroke: '#fff', strokeWidth: 2 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                <Typography color="text.secondary">No activity data available yet.</Typography>
                            </Box>
                        )}
                    </SectionCard>
                </Grid>

                {/* Donut Chart */}
                <Grid item xs={12} md={4}>
                    <SectionCard title="Language Distribution" subtitle="By word count">
                        {donutData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie data={donutData} cx="50%" cy="45%" innerRadius={65} outerRadius={95}
                                        paddingAngle={3} dataKey="value" nameKey="name" strokeWidth={0}>
                                        {donutData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                        <DonutCenterLabel total={totalLangWords} />
                                    </Pie>
                                    <RTooltip content={<CustomTooltip />} />
                                    <Legend verticalAlign="bottom" iconType="circle" iconSize={8}
                                        formatter={(value) => (
                                            <span style={{ color: theme.palette.text.primary, fontSize: '0.8rem', fontWeight: 500 }}>
                                                {value}
                                            </span>
                                        )} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                <Typography color="text.secondary">No language data available.</Typography>
                            </Box>
                        )}
                    </SectionCard>
                </Grid>
            </Grid>

            {/* ─── Projects by Day Bar Chart + Recent Projects Table ─ */}
            <Grid container spacing={2.5}>
                {/* Mini bar chart for projects per day */}
                <Grid item xs={12} md={4}>
                    <SectionCard title="Projects Created" subtitle={`Last ${timeRange} days`}>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                    axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                    axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                                <RTooltip content={<CustomTooltip />} />
                                <Bar dataKey="projects" name="Projects" fill={theme.palette.secondary.main}
                                    radius={[4, 4, 0, 0]} maxBarSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </SectionCard>
                </Grid>

                {/* Recent Projects Table */}
                <Grid item xs={12} md={8}>
                    <SectionCard title="Recent Projects" subtitle="Latest activity across your workspace">
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {['Project', 'Status', 'Words', 'Last Updated'].map((h) => (
                                            <TableCell key={h} sx={{
                                                fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase',
                                                letterSpacing: '0.05em', color: 'text.secondary',
                                                bgcolor: alpha(theme.palette.primary.main, 0.03),
                                                borderBottom: `2px solid ${theme.palette.divider}`,
                                            }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recentProjects.length > 0 ? (
                                        recentProjects.slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage).map((project) => {
                                            const status = project.status || 'pending';
                                            const colorMap = { completed: 'success', 'in-progress': 'warning', pending: 'default' };
                                            return (
                                                <TableRow key={project._id} hover sx={{
                                                    transition: 'background 0.2s ease',
                                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                                                    '& td': { borderColor: theme.palette.divider }
                                                }}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{project.projectName}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={status.charAt(0).toUpperCase() + status.slice(1)}
                                                            size="small" color={colorMap[status] || 'default'} variant="outlined"
                                                            sx={{ fontWeight: 600, fontSize: '0.72rem' }} />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2">{(project.wordCount || 0).toLocaleString()}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(project.updatedAt || project.createdAt).toLocaleDateString()}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                No recent projects found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {recentProjects.length > rowsPerPage && (
                            <TablePagination
                                component="div" count={recentProjects.length}
                                page={tablePage} onPageChange={(_, p) => setTablePage(p)}
                                rowsPerPage={rowsPerPage} rowsPerPageOptions={[5]}
                                sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                            />
                        )}
                    </SectionCard>
                </Grid>
            </Grid>
        </Container>
    );
}

// ─── Helper ──────────────────────────────────────────────────────────
function formatDateLabel(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
